// The Sundargarh (live KoboToolbox) counterpart of ../config/rules.js.
// Same 20-rule catalog and category grouping (../config/categories.js is
// reused as-is), but scored against live Kobo submissions grouped by
// ULB / Ward / Enumerator instead of the internal DB's Block / GP / Village.
//
// enabled:true only where the rule is computable from data this Kobo form
// actually has:
//  - "respondent-not-found" is RE-DERIVED: matches the respondent's name
//    against their OWN household's roster in the same submission
//    (member_name1..9), not an external member-master list (this Kobo form
//    has no such external roster).
//  - "school-count-wrong" is RE-DERIVED: flags scholarship-receiving count
//    exceeding the eligible count (this form has no auto-calculated
//    expected-in-school field to compare against).
//  - "institutional-delivery" stays disabled: sampled data shows the
//    underlying field holding inconsistent value types across submissions,
//    not reliable enough to validate on.
//  - the 4 rules already disabled in the internal catalog stay disabled here
//    too (never had a real implementation to port).

export const sundargarhRules = [
  {
    id: 'respondent-not-found',
    number: 1,
    title: 'Respondent not found in members',
    category: 'household',
    source: 'Name match against the submission\'s own member_name1..9',
    enabled: true,
    component: 'memberMatch',
    description: 'Flags surveys where the respondent name does not match any member listed on that same household\'s roster.',
  },
  {
    id: 'school-count-wrong',
    number: 2,
    title: 'Children-in-school count wrong',
    category: 'household',
    source: 'children_count_receiving_scola vs children_count_eligible',
    enabled: true,
    component: 'schoolCount',
    description: 'Flags surveys where the recorded scholarship-receiving children count exceeds the eligible count.',
  },
  {
    id: 'institutional-delivery',
    number: 3,
    title: 'Institutional delivery vs children count',
    category: 'women-child',
    source: 'institutional_delivery',
    enabled: false,
  },
  {
    id: 'anganwadi-nutrition',
    number: 4,
    title: 'Anganwadi registered but nutrition N/A',
    category: 'women-child',
    source: 'compute (anganwadi_registration vs supplementary_nutrition_receiv / take_home_ration_received)',
    enabled: true,
    component: 'anganwadi',
    description: "Among Anganwadi-registered households, flags what share have Supplementary Nutrition or Take-Home Ration marked 'Not applicable' — inconsistent with being registered.",
  },
  {
    id: 'subhadra-eligible',
    number: 5,
    title: 'Subhadra eligible women count wrong',
    category: 'subhadra',
    source: 'compute (subhadra_beneficiary_count vs subhadra_eligible_women_count)',
    enabled: true,
    component: 'subhadra',
    description: 'Flags households where the reported Subhadra beneficiary count exceeds the reported eligible-women count.',
  },
  {
    id: 'gender-relation-mismatch',
    number: 6,
    title: 'Gender vs relation mismatch',
    category: 'gender',
    source: 'compute (member roster)',
    enabled: false,
  },
  {
    id: 'member-name-invalid',
    number: 7,
    title: 'Member name incomplete/invalid',
    category: 'household',
    source: 'compute / member *_warning',
    enabled: false,
  },
  {
    id: 'high-no-skill-training',
    number: 8,
    title: "Enumerator high 'no skill training' rate",
    category: 'skill-livelihood',
    source: 'aggregate rate (threshold later)',
    enabled: true,
    component: 'indicators',
    fieldKey: 'skill_training_required',
    description: 'Yes/No breakdown for whether skill training is required.',
  },
  {
    id: 'respondent-gender-roster',
    number: 9,
    title: 'Respondent gender vs roster',
    category: 'household',
    source: 'compute (member roster)',
    enabled: true,
    component: 'genderRoster',
    description: 'Flags households where a member\'s recorded gender does not match what their stated relation (Son, Daughter, Wife, Husband, etc.) implies.',
  },
  {
    id: 'duplicate-member-name',
    number: 10,
    title: 'Same member name repeated',
    category: 'household',
    source: 'compute (members)',
    enabled: true,
    component: 'duplicateMember',
    description: "Flags survey submissions where two household members' names are 75% or more similar to each other — likely the same person entered twice.",
  },
  {
    id: 'high-no-insurance',
    number: 11,
    title: "Enumerator high 'None' insurance rate",
    category: 'health',
    source: 'aggregate rate',
    enabled: true,
    component: 'healthInsurance',
    description: "All / Partial / None breakdown for household health insurance coverage, highlighting a high 'None' rate.",
  },
  {
    id: 'age-education-mismatch',
    number: 12,
    title: 'Age too low for education level',
    category: 'education',
    source: 'compute (literacy_memberN vs Age_Member_N)',
    enabled: true,
    component: 'ageEducation',
    description: 'Flags household members whose age is implausibly low for their reported education level (Secondary >12, Higher Secondary >16, Graduation >18, Post Graduation >20).',
  },
  {
    id: 'high-no-agriculture',
    number: 13,
    title: "Enumerator high 'no agriculture' rate",
    category: 'skill-livelihood',
    source: 'aggregate rate',
    enabled: true,
    component: 'indicators',
    fieldKey: 'agriculture_practiced',
    description: 'Yes/No breakdown for whether agriculture is practiced.',
  },
  {
    id: 'high-no-livestock',
    number: 14,
    title: "Enumerator high 'no livestock' rate",
    category: 'skill-livelihood',
    source: 'aggregate rate',
    enabled: true,
    component: 'indicators',
    fieldKey: 'livestock_available',
    description: 'Yes/No breakdown for livestock availability.',
  },
  {
    id: 'multiple-spouse',
    number: 15,
    title: '>1 husband or wife in household',
    category: 'household',
    source: 'compute (members)',
    enabled: true,
    component: 'multiSpouse',
    description: 'Flags households with more than one member recorded as Husband or Wife.',
  },
  {
    id: 'parent-unmarried',
    number: 16,
    title: 'Parent marked unmarried',
    category: 'household',
    source: 'compute (members)',
    enabled: false,
  },
  {
    id: 'high-no-own-house',
    number: 17,
    title: "Enumerator high 'no own house' rate",
    category: 'housing',
    source: 'aggregate rate',
    enabled: true,
    component: 'indicators',
    fieldKey: 'own_house',
    description: 'Yes/No breakdown for whether the household owns its house.',
  },
  {
    id: 'member-count-mismatch',
    number: 18,
    title: 'Total member count vs roster',
    category: 'household',
    source: 'compute (Total_No_of_Household_Members vs member_name1..9 count)',
    enabled: true,
    component: 'roster',
    description: 'Compares the stated total household members against the actual number of members recorded on the roster, bucketed by match completeness.',
  },
  {
    id: 'small-households',
    number: 19,
    title: 'Too many 1–2 member households',
    category: 'household',
    source: 'aggregate rate',
    enabled: true,
    component: 'smallHouseholds',
    description: 'Flags an unusually high rate of 1-2 member households per enumerator, ULB, or ward.',
  },
  {
    id: 'gender-extended-relation',
    number: 20,
    title: 'Gender vs extended relation',
    category: 'gender',
    source: 'compute (members)',
    enabled: false,
  },
];

export const getSundargarhRuleById = (id) => sundargarhRules.find((r) => r.id === id) || null;
export const getSundargarhRulesByCategory = (categoryId) => sundargarhRules.filter((r) => r.category === categoryId);
export const getSundargarhCategoryRuleCounts = (categoryId) => {
  const list = getSundargarhRulesByCategory(categoryId);
  return { total: list.length, available: list.filter((r) => r.enabled).length };
};

export default sundargarhRules;
